// The compare tray: the products picked for comparison, six at most, each
// with its name. It is kept for the browser session and shared by every
// place a product can be picked (Explore, the product page, the Analytics
// matrix) and by the compare page, through one store and the hook
// `useCompareTray`. Without a session storage the tray still works; it is
// then forgotten on a reload.

import { useMemo, useSyncExternalStore } from "react";

import { MAX_COMPARED } from "./compareState";

// The key of the tray in the session storage
export const TRAY_STORAGE_KEY = "aa.compare.tray";

// One product in the tray
export interface TrayEntry {
  id: string;
  // Null for a product no source names, and for one whose name is not known
  // yet
  name: string | null;
}

// The tray outside React: what `useCompareTray` is built on
export interface CompareTrayStore {
  // Calls the listener after every change; returns the function that stops
  // it
  subscribe: (listener: () => void) => () => void;
  // The entries in the order picked. The same list until the tray changes.
  getSnapshot: () => readonly TrayEntry[];
  // Adds a product at the end. Returns false, and adds nothing, when the
  // tray already holds six others: the caller says so to the user. A
  // product already held stays where it is (its name is filled in when it
  // was not known) and the answer is true.
  add: (entry: TrayEntry) => boolean;
  remove: (id: string) => void;
  // Takes a product out when the tray holds it, else adds it. Returns false
  // only when it could not be added because the tray is full.
  toggle: (entry: TrayEntry) => boolean;
  clear: () => void;
  // Sets the tray to these entries: repeats dropped, cut to the first six
  replace: (entries: readonly TrayEntry[]) => void;
}

const EMPTY: readonly TrayEntry[] = [];

// The entries as the tray holds them: each product once, six at most
const bound = (entries: readonly TrayEntry[]): TrayEntry[] => {
  const seen = new Set<string>();
  const kept: TrayEntry[] = [];
  for (const { id, name } of entries) {
    if (id && !seen.has(id) && kept.length < MAX_COMPARED) {
      seen.add(id);
      kept.push({ id, name });
    }
  }
  return kept;
};

// What a stored text holds; anything that is not a list of entries reads as
// an empty tray, and an entry without an id is left out
const parse = (text: string | null): TrayEntry[] => {
  const value: unknown = text ? JSON.parse(text) : null;
  if (!Array.isArray(value)) {
    return [];
  }
  return bound(
    value.flatMap((item: unknown): TrayEntry[] => {
      const { id, name } = (item ?? {}) as { id?: unknown; name?: unknown };
      return typeof id === "string"
        ? [{ id, name: typeof name === "string" ? name : null }]
        : [];
    }),
  );
};

const same = (
  left: readonly TrayEntry[],
  right: readonly TrayEntry[],
): boolean =>
  left.length === right.length &&
  left.every(
    (entry, index) =>
      entry.id === right[index].id && entry.name === right[index].name,
  );

/**
 * Creates a tray over a storage.
 *
 * The app uses the one tray `compareTrayStore`; a tray of its own is for a
 * test. Every read and write of the storage may fail (a blocked or full
 * storage): the tray then lives in memory alone.
 *
 * @param storage - Gives the storage to keep the tray in; it may throw
 * @returns The store
 */
export const createCompareTrayStore = (
  storage: () => Storage = () => window.sessionStorage,
): CompareTrayStore => {
  // Null until the first read: the storage is read once, when first asked
  let items: readonly TrayEntry[] | null = null;
  const listeners = new Set<() => void>();

  const read = (): readonly TrayEntry[] => {
    if (items === null) {
      try {
        items = parse(storage().getItem(TRAY_STORAGE_KEY));
      } catch {
        items = EMPTY;
      }
    }
    return items;
  };

  const write = (next: readonly TrayEntry[]): void => {
    if (same(read(), next)) {
      return;
    }
    items = next;
    try {
      if (next.length > 0) {
        storage().setItem(TRAY_STORAGE_KEY, JSON.stringify(next));
      } else {
        storage().removeItem(TRAY_STORAGE_KEY);
      }
    } catch {
      // A full or blocked storage keeps nothing; the tray still holds it
    }
    listeners.forEach((listener) => listener());
  };

  const add = (entry: TrayEntry): boolean => {
    const current = read();
    const held = current.find((item) => item.id === entry.id);
    if (held) {
      if (held.name === null && entry.name !== null) {
        write(
          current.map((item) =>
            item.id === entry.id ? { ...item, name: entry.name } : item,
          ),
        );
      }
      return true;
    }
    if (!entry.id || current.length >= MAX_COMPARED) {
      return false;
    }
    write([...current, { id: entry.id, name: entry.name }]);
    return true;
  };

  const remove = (id: string): void => {
    write(read().filter((item) => item.id !== id));
  };

  return {
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    getSnapshot: read,
    add,
    remove,
    toggle: (entry) => {
      if (read().some((item) => item.id === entry.id)) {
        remove(entry.id);
        return true;
      }
      return add(entry);
    },
    clear: () => write(EMPTY),
    replace: (entries) => write(bound(entries)),
  };
};

// The tray of the app
export const compareTrayStore: CompareTrayStore = createCompareTrayStore();

// The tray as a component uses it. The functions are those of the store and
// stay the same between renders.
export interface CompareTray {
  // The products picked, in the order picked
  items: readonly TrayEntry[];
  // True when the tray holds six products: one more would be refused
  full: boolean;
  has: (id: string) => boolean;
  // Returns false, and adds nothing, when the tray is full: say so
  add: (entry: TrayEntry) => boolean;
  remove: (id: string) => void;
  // Returns false only when the product could not be added because the
  // tray is full
  toggle: (entry: TrayEntry) => boolean;
  clear: () => void;
  replace: (entries: readonly TrayEntry[]) => void;
}

/**
 * The compare tray, shared by every component that uses the hook.
 *
 * @returns The products picked, and the functions that change the pick
 */
export function useCompareTray(): CompareTray {
  const items = useSyncExternalStore(
    compareTrayStore.subscribe,
    compareTrayStore.getSnapshot,
  );

  return useMemo(
    () => ({
      items,
      full: items.length >= MAX_COMPARED,
      has: (id: string) => items.some((item) => item.id === id),
      add: compareTrayStore.add,
      remove: compareTrayStore.remove,
      toggle: compareTrayStore.toggle,
      clear: compareTrayStore.clear,
      replace: compareTrayStore.replace,
    }),
    [items],
  );
}
